import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { provider_id: "", npi: "", jurisdiction: "DC", enrollment_id: "", effective_date: "", status: "Pending", revalidation_due_date: "" };

export const MedicaidForm = ({ initial, providers, catalog, onSaved, onCancel }) => {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [error, setError] = useState("");
  const set = (patch) => setForm({ ...form, ...patch });
  async function submit(event) {
    event.preventDefault();
    try {
      const body = { provider_id: form.provider_id, npi: form.npi, jurisdiction: form.jurisdiction, enrollment_id: form.enrollment_id, effective_date: form.effective_date, status: form.status, revalidation_due_date: form.revalidation_due_date };
      const response = initial?.id ? await api.put(`/compliance/medicaid/${initial.id}`, body) : await api.post("/compliance/medicaid", body);
      onSaved(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }
  return (
    <form className="inline-edit-form medicaid-form" data-testid="medicaid-form" onSubmit={submit}>
      <label>Provider
        <select required value={form.provider_id} data-testid="medicaid-provider-select" disabled={!!initial?.id} onChange={(e) => set({ provider_id: e.target.value })}>
          <option value="">Select provider</option>
          {providers.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.discipline}</option>)}
        </select>
      </label>
      <label>NPI<input value={form.npi} maxLength={10} placeholder="10 digits" data-testid="medicaid-npi-input" onChange={(e) => set({ npi: e.target.value })} /></label>
      <label>Jurisdiction
        <select value={form.jurisdiction} data-testid="medicaid-jurisdiction-select" onChange={(e) => set({ jurisdiction: e.target.value })}>{catalog.jurisdictions.map((j) => <option key={j}>{j}</option>)}</select>
      </label>
      <label>Enrollment ID<input value={form.enrollment_id} data-testid="medicaid-enrollment-input" onChange={(e) => set({ enrollment_id: e.target.value })} /></label>
      <label>Effective date<input type="date" value={form.effective_date} data-testid="medicaid-effective-input" onChange={(e) => set({ effective_date: e.target.value })} /></label>
      <label>Status
        <select value={form.status} data-testid="medicaid-status-select" onChange={(e) => set({ status: e.target.value })}>{catalog.medicaid_statuses.map((s) => <option key={s}>{s}</option>)}</select>
      </label>
      <label>Revalidation due<input type="date" value={form.revalidation_due_date} data-testid="medicaid-revalidation-input" onChange={(e) => set({ revalidation_due_date: e.target.value })} /></label>
      {error && <p className="form-message error-message" data-testid="medicaid-form-error">{error}</p>}
      <div className="button-row">
        <button className="button button-primary" data-testid="save-medicaid-button"><Plus size={14} /> {initial?.id ? "Save enrollment" : "Add Medicaid enrollment"}</button>
        <button type="button" className="button button-secondary" data-testid="cancel-medicaid-button" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

export const MedicaidRow = ({ record, canManage, onEdit, onDeleted }) => {
  const [error, setError] = useState("");
  async function remove() {
    try {
      await api.delete(`/compliance/medicaid/${record.id}`);
      onDeleted(record);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }
  const level = record.alert_level ? ` · HR alert (${record.alert_level}-day)` : "";
  return (
    <tr className="credential-group-child medicaid-row" data-testid={`medicaid-record-${record.id}`}>
      <td><b>{record.provider_name}</b><span>{record.provider_discipline}</span></td>
      <td>Medicaid enrollment · {record.jurisdiction}<span data-testid={`medicaid-detail-${record.id}`}>NPI {record.npi || "—"} · ID {record.enrollment_id || "—"} · effective {record.effective_date || "—"}</span></td>
      <td>{record.revalidation_due_date || "—"}<span>{record.days_to_revalidation === null ? "Revalidation not set" : `${record.days_to_revalidation} days to revalidation${level}`}</span></td>
      <td>—</td>
      <td><em className={`compliance-result compliance-result-${record.effective_status.toLowerCase()}`} data-testid={`medicaid-status-${record.id}`}>{record.effective_status}</em></td>
      <td>{record.effective_status === "Pending" ? "Sessions not billable" : record.effective_status === "Active" ? "Billable" : "Not billable"}</td>
      <td>—</td>
      {canManage && (
        <td className="credential-actions">
          <button type="button" className="table-edit-button" data-testid={`edit-medicaid-${record.id}`} onClick={() => onEdit(record)}><Pencil size={14} /> Edit</button>
          <button type="button" className="table-edit-button table-delete-button" data-testid={`delete-medicaid-${record.id}`} onClick={remove}><Trash2 size={14} /> Delete</button>
          {error && <small className="form-message error-message">{error}</small>}
        </td>
      )}
    </tr>
  );
};
